import React, { useEffect, useState, useRef } from 'react';
export const SupportPage = () => {
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [showWelcome, setShowWelcome] = useState(true);
  const chatMessagesRef = useRef(null);
  const inputRef = useRef(null);
  // Knowledge base for Mayhavecopy.com
  const knowledgeBase = {
    services: {
      keywords: ['service', 'offer', 'do', 'copy', 'write', 'content'],
      response: 'We offer comprehensive copywriting services including:\n\n• Website copy & landing pages\n• Sales emails & sequences\n• Product descriptions\n• Blog posts & articles\n• Social media content\n• Ad copy (Google, Facebook, etc.)\n• Press releases\n• Case studies & testimonials\n\nAll copy is written by experienced professionals and tailored to your brand voice!'
    },
    pricing: {
      keywords: ['price', 'cost', 'pricing', 'expensive', 'cheap', 'rate', 'fee'],
      response: 'Our pricing is project-based and depends on scope:\n\n💰 **Starter Package**: $299-599\n• Landing page or small website\n• Up to 1,000 words\n\n💼 **Professional Package**: $599-1,299\n• Complete website copy\n• Email sequence (5-7 emails)\n• Up to 3,000 words\n\n🚀 **Enterprise Package**: $1,299+\n• Full content strategy\n• Multiple pages/campaigns\n• Ongoing support\n\nWe offer free consultations to provide accurate quotes!'
    },
    turnaround: {
      keywords: ['time', 'fast', 'quick', 'delivery', 'turnaround', 'rush', 'urgent'],
      response: 'Our typical turnaround times:\n\n⚡ **Rush Orders**: 24-48 hours (+50% fee)\n📅 **Standard**: 3-5 business days\n📋 **Large Projects**: 7-14 business days\n\nWe always communicate timeline expectations upfront and provide regular updates on your project status!'
    },
    process: {
      keywords: ['process', 'how', 'work', 'start', 'begin', 'steps'],
      response: "Here's how we work together:\n\n1. **Free Consultation** - We discuss your needs\n2. **Project Proposal** - Detailed scope & timeline\n3. **50% Deposit** - Secures your project slot\n4. **Research & Writing** - We create your copy\n5. **Review & Revisions** - Up to 3 rounds included\n6. **Final Delivery** - You receive completed copy\n\nReady to get started? Just let me know what you need!"
    },
    revisions: {
      keywords: ['revision', 'change', 'edit', 'modify', 'update'],
      response: 'We include up to 3 rounds of revisions with every project at no extra cost! 📝\n\nRevisions can include:\n• Tone adjustments\n• Content modifications\n• Structure changes\n• Minor rewrites\n\nAdditional revisions beyond the 3 included rounds are available at $75/hour.'
    },
    contact: {
      keywords: ['contact', 'reach', 'phone', 'email', 'talk', 'speak'],
      response: "Here's how to reach us:\n\n📧 **Email**: hello@mayhavecopy.com\n📞 **Phone**: (555) 123-COPY\n💬 **Live Chat**: Right here!\n🕐 **Hours**: Mon-Fri 9AM-6PM EST\n\nFor urgent requests, email is usually fastest. We respond within 2 hours during business hours!"
    },
    samples: {
      keywords: ['sample', 'example', 'portfolio', 'work', 'previous'],
      response: "Absolutely! We'd love to show you our work:\n\n🎯 **Portfolio**: Check out samples at mayhavecopy.com/portfolio\n📄 **Case Studies**: See results we've achieved for clients\n✍️ **Writing Samples**: Available for your specific industry\n\nI can also arrange a call with one of our copywriters to discuss your project and share relevant samples!"
    }
  };
  useEffect(() => {
    // Focus input on load
    if (inputRef.current) {
      inputRef.current.focus();
    }
  }, []);
  useEffect(() => {
    // Scroll to bottom when messages change
    if (chatMessagesRef.current) {
      chatMessagesRef.current.scrollTop = chatMessagesRef.current.scrollHeight;
    }
  }, [messages, isTyping]);
  const findBestResponse = userMessage => {
    const message = userMessage.toLowerCase();
    let bestMatch = null;
    let maxMatches = 0;
    for (const [category, data] of Object.entries(knowledgeBase)) {
      const matches = data.keywords.filter(keyword => message.includes(keyword)).length;
      if (matches > maxMatches) {
        maxMatches = matches;
        bestMatch = data;
      }
    }
    if (bestMatch) {
      return bestMatch.response;
    }
    // Default responses for common queries
    if (message.includes('hello') || message.includes('hi') || message.includes('hey')) {
      return "Hello! Welcome to Mayhavecopy.com! 👋 I'm here to help you with any questions about our copywriting services. What can I assist you with today?";
    }
    if (message.includes('thank') || message.includes('thanks')) {
      return "You're very welcome! Is there anything else I can help you with regarding our copywriting services? I'm here whenever you need assistance! 😊";
    }
    if (message.includes('bye') || message.includes('goodbye')) {
      return "Thank you for contacting Mayhavecopy.com! Have a great day, and don't hesitate to reach out if you need any copywriting help in the future! 👋";
    }
    // Fallback response
    return "That's a great question! While I'd love to help with everything, I specialize in questions about our copywriting services, pricing, turnaround times, and process.\n\nFor more specific inquiries, I'd recommend:\n• Emailing us at hello@mayhavecopy.com\n• Calling (555) 123-COPY\n• Scheduling a free consultation\n\nIs there anything else about our copywriting services I can help clarify?";
  };
  const sendMessage = () => {
    if (!inputValue.trim()) return;
    // Clear welcome message
    if (showWelcome) {
      setShowWelcome(false);
    }
    // Add user message
    setMessages(prev => [...prev, {
      content: inputValue,
      sender: 'user'
    }]);
    setInputValue('');
    // Show typing indicator
    setIsTyping(true);
    // Generate bot response with delay
    setTimeout(() => {
      const response = findBestResponse(inputValue);
      setIsTyping(false);
      setMessages(prev => [...prev, {
        content: response,
        sender: 'bot'
      }]);
      if (inputRef.current) {
        inputRef.current.focus();
      }
    }, 1000 + Math.random() * 1000); // Random delay between 1-2 seconds
  };
  const sendQuickMessage = message => {
    setInputValue(message);
    setTimeout(() => {
      sendMessage();
    }, 10);
  };
  const handleKeyPress = event => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      sendMessage();
    }
  };
  return <div className="w-full min-h-screen bg-gradient-to-br from-[#667eea] to-[#764ba2] flex justify-center items-center p-5" data-id="element-377">
      <div className="bg-white rounded-3xl shadow-2xl w-full max-w-4xl h-[600px] flex flex-col overflow-hidden" data-id="element-378">
        <div className="bg-gradient-to-r from-[#4f46e5] to-[#7c3aed] text-white p-5 text-center relative" data-id="element-379">
          <h1 className="text-2xl font-bold mb-1" data-id="element-380">Mayhavecopy.com Support</h1>
          <p className="text-sm opacity-90" data-id="element-381">AI Assistant - Here to help 24/7</p>
          <div className="absolute top-5 right-5 flex items-center gap-2" data-id="element-382">
            <div className="w-2.5 h-2.5 bg-green-500 rounded-full animate-pulse" data-id="element-383"></div>
            <span className="text-sm" data-id="element-384">Online</span>
          </div>
        </div>
        <div ref={chatMessagesRef} className="flex-1 p-5 overflow-y-auto flex flex-col gap-4 bg-gray-50" data-id="element-385">
          {showWelcome && <div className="text-center py-10 text-gray-500" data-id="element-386">
              <h2 className="text-xl font-bold text-gray-700 mb-2" data-id="element-387">
                Welcome to Mayhavecopy.com Support! 👋
              </h2>
              <p data-id="element-388">
                I'm your AI assistant, ready to help with questions about our
                copywriting services, pricing, turnaround times, and more. How
                can I assist you today?
              </p>
            </div>}
          {messages.map((msg, index) => <div key={index} className={`flex gap-3 max-w-[85%] ${msg.sender === 'user' ? 'self-end flex-row-reverse' : 'self-start'} animate-[slideIn_0.3s_ease-out]`} data-id="element-389">
              <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold text-white flex-shrink-0 ${msg.sender === 'user' ? 'bg-gradient-to-br from-amber-500 to-red-500' : 'bg-gradient-to-br from-blue-500 to-purple-500'}`} data-id="element-390">
                {msg.sender === 'user' ? 'U' : 'AI'}
              </div>
              <div className={`p-4 rounded-2xl shadow-sm ${msg.sender === 'user' ? 'bg-gradient-to-r from-[#4f46e5] to-[#7c3aed] text-white' : 'bg-white'}`} dangerouslySetInnerHTML={{
            __html: msg.content.replace(/\n/g, '<br>')
          }} data-id="element-391"></div>
            </div>)}
          {isTyping && <div className="flex items-center gap-3 ml-[52px]" data-id="element-392">
              <div className="flex gap-1" data-id="element-393">
                <div className="w-2 h-2 bg-gray-500 rounded-full animate-bounce" data-id="element-394"></div>
                <div className="w-2 h-2 bg-gray-500 rounded-full animate-bounce" style={{
              animationDelay: '0.2s'
            }} data-id="element-395"></div>
                <div className="w-2 h-2 bg-gray-500 rounded-full animate-bounce" style={{
              animationDelay: '0.4s'
            }} data-id="element-396"></div>
              </div>
              <span className="text-sm text-gray-500" data-id="element-397">
                AI Assistant is typing...
              </span>
            </div>}
        </div>
        <div className="p-4 bg-gray-100 border-t border-gray-200 flex flex-wrap gap-2" data-id="element-398">
          <button className="px-4 py-2 bg-white border border-gray-300 rounded-full text-sm hover:bg-indigo-600 hover:text-white hover:border-indigo-600 transition-colors" onClick={() => sendQuickMessage('What services do you offer?')} data-id="element-399">
            Our Services
          </button>
          <button className="px-4 py-2 bg-white border border-gray-300 rounded-full text-sm hover:bg-indigo-600 hover:text-white hover:border-indigo-600 transition-colors" onClick={() => sendQuickMessage('What are your pricing options?')} data-id="element-400">
            Pricing
          </button>
          <button className="px-4 py-2 bg-white border border-gray-300 rounded-full text-sm hover:bg-indigo-600 hover:text-white hover:border-indigo-600 transition-colors" onClick={() => sendQuickMessage('How long does it take to get my copy?')} data-id="element-401">
            Turnaround Time
          </button>
          <button className="px-4 py-2 bg-white border border-gray-300 rounded-full text-sm hover:bg-indigo-600 hover:text-white hover:border-indigo-600 transition-colors" onClick={() => sendQuickMessage('I need help with my order')} data-id="element-402">
            Order Help
          </button>
        </div>
        <div className="p-5 bg-white border-t border-gray-200 flex gap-3" data-id="element-403">
          <input ref={inputRef} type="text" className="flex-1 p-4 border-2 border-gray-200 rounded-full outline-none focus:border-indigo-600 focus:ring-2 focus:ring-indigo-100 text-base" placeholder="Type your message here..." value={inputValue} onChange={e => setInputValue(e.target.value)} onKeyPress={handleKeyPress} data-id="element-404" />
          <button className="px-6 py-4 bg-gradient-to-r from-[#4f46e5] to-[#7c3aed] text-white font-bold rounded-full flex items-center gap-2 hover:translate-y-[-2px] hover:shadow-lg transition-all disabled:opacity-60 disabled:cursor-not-allowed disabled:transform-none" onClick={sendMessage} disabled={isTyping || !inputValue.trim()} data-id="element-405">
            <span data-id="element-406">Send</span>
            <span data-id="element-407">→</span>
          </button>
        </div>
      </div>
    </div>;
};